import { getTenantConfig, type TenantConfig } from '@dmp/api';
import { type AxiosError } from 'axios';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { cacheTenantConfig, getCachedTenantConfig } from './cache';
import { DOMAIN_MAP } from './rbac';

dayjs.extend(utc);

export const DEFAULT_TENANT = 'CENTIFIC';

export const DEFAULT_TENANT_CONFIG: TenantConfig = {
  tenant_name: '',
  enforce_login: false,
  allowed_domains: [],
  allow_sso: true,
  allow_company_edit: true,
  allow_remember_me: true,
  personas: [],
  entra_auth_enabled: false,
};

export const getTenantNameFromDomain = () => {
  const host = window.location.host;
  const hostPrefix = host.split('.centific.com')[0] ?? '';
  const [, tenantName] = hostPrefix.split('-');

  if (!tenantName) {
    return DEFAULT_TENANT;
  }

  if (['dev', 'qa', 'stg'].includes(tenantName)) {
    return DEFAULT_TENANT;
  }

  return tenantName;
};

export const getBeApiDomain = () => {
  if (window.location.hostname === 'localhost') {
    return import.meta.env.DATA_MARKETPLACE_API_URL;
  }

  const host = window.location.host;
  const hostPrefix = host.split('.centific.com')[0] ?? '';

  return `${window.location.protocol}//api-${hostPrefix}.centific.com`;
};

export const fetchTenantConfig = async () => {
  const data = getCachedTenantConfig();

  if (data) {
    const { refreshTime, config } = data;
    const now = new Date().toISOString();
    const diff = dayjs.utc(now).diff(dayjs.utc(refreshTime), 'second');
    // within 1 hour, use cache
    if (diff >= 0 && diff < 3600) {
      return config;
    }
  }

  try {
    const res = await getTenantConfig();

    const config = res.data;

    cacheTenantConfig(config);

    return config;
  } catch (error) {
    throw error as AxiosError;
  }
};

export const isTenantEnvironment = () => {
  if (window.location.hostname === 'localhost') {
    return import.meta.env.DATA_MARKETPLACE_IS_TENANT_ENV.toUpperCase() === 'TRUE';
  }
  const origin = window.location.origin;

  const globalDmpDomains = Object.values(DOMAIN_MAP);

  return !globalDmpDomains.includes(origin);
};
